<template>
  <div class="player-nutrition-detail">
    <!-- Header -->
    <div class="detail-header">
      <button class="back-btn" @click="goBack">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        <span>Players</span>
      </button>
      <div class="player-header-info">
        <div class="player-avatar">
          <img
            v-if="player.image"
            :src="player.image"
            :alt="player.name"
            class="avatar-img"
          />
          <div v-else class="avatar-placeholder">
            {{ getInitials(player.name) }}
          </div>
        </div>
        <div class="player-meta">
          <h2 class="player-name">{{ player.name }}</h2>
          <span v-if="player.team_name" class="player-team">{{ player.team_name }}</span>
        </div>
      </div>
    </div>

    <!-- Date selector -->
    <div class="date-selector">
      <button class="date-nav" @click="changeDate(-1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <div class="date-display" @click="showDatePicker = true">
        <span class="date-label">{{ formatDateDisplay(currentDate) }}</span>
        <span v-if="isToday" class="today-badge">Today</span>
      </div>
      <button class="date-nav" @click="changeDate(1)" :disabled="isToday">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>

    <!-- Tabs -->
    <div class="detail-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab-btn', { active: activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab content -->
    <div class="tab-content">
      <!-- Today Tab -->
      <div v-if="activeTab === 'today'" class="today-tab">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <span>Loading nutrition data...</span>
        </div>

        <template v-else>
          <!-- Quick Summary -->
          <div class="quick-summary">
            <NutritionProgress
              v-if="dailyData"
              :totals="dailyData.totals"
              :targets="dailyData.targets"
              :completion="dailyData.completion"
              :context="dailyData.context"
              :show-overall="true"
              :show-calories="true"
              :show-context="true"
              energy-framing="neutral"
            />
          </div>

          <!-- Hydration Summary -->
          <div class="hydration-card">
            <div class="card-header">
              <h3>Hydration</h3>
              <span class="hydration-value">
                {{ formatValue(hydrationTotal) }} / {{ formatValue(hydrationTarget) }}ml
              </span>
            </div>
            <div class="hydration-bar">
              <div
                class="hydration-fill"
                :style="{ width: hydrationPercent + '%' }"
              ></div>
            </div>
            <span class="hydration-percent">{{ hydrationPercent }}%</span>
          </div>

          <!-- Meals List -->
          <div class="meals-section">
            <h3>Meals Logged</h3>
            <div v-if="meals.length > 0" class="meals-list">
              <div v-for="meal in meals" :key="meal.id" class="meal-item">
                <div class="meal-icon" :style="getMealIconStyle(meal.meal_type)">
                  {{ getMealSymbol(meal.meal_type) }}
                </div>
                <div class="meal-info">
                  <span class="meal-name">{{ meal.meal_name || getMealLabel(meal.meal_type) }}</span>
                  <span class="meal-time">{{ formatTime(meal.meal_time) }}</span>
                </div>
                <div class="meal-macros">
                  <span>{{ formatValue(meal.calories) }} kcal</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-meals">
              <p>No meals logged for this day</p>
            </div>
          </div>
        </template>
      </div>

      <!-- Week Tab -->
      <div v-if="activeTab === 'week'" class="week-tab">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <span>Loading weekly data...</span>
        </div>

        <template v-else>
          <!-- Weekly chart / summary would go here -->
          <div class="week-summary">
            <div class="week-header">
              <h3>This Week's Overview</h3>
            </div>

            <div class="week-days">
              <div
                v-for="day in weekDays"
                :key="day.date"
                :class="['day-card', { today: day.isToday }]"
                @click="selectDay(day.date)"
              >
                <span class="day-name">{{ day.dayName }}</span>
                <span class="day-date">{{ day.dayNum }}</span>
                <div class="day-status" :style="getDayStatusStyle(day)">
                  <span v-if="day.mealsLogged > 0">{{ day.mealsLogged }}</span>
                  <span v-else class="day-empty">-</span>
                </div>
              </div>
            </div>

            <!-- Week totals -->
            <div class="week-totals">
              <div class="total-item">
                <span class="total-label">Avg. Hydration</span>
                <span class="total-value">{{ weeklyData.avgHydration || 0 }}%</span>
              </div>
              <div class="total-item">
                <span class="total-label">Meals This Week</span>
                <span class="total-value">{{ weeklyData.totalMeals || 0 }}</span>
              </div>
              <div class="total-item">
                <span class="total-label">Compliance</span>
                <span class="total-value">{{ weeklyData.compliance || 0 }}%</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Goals Tab -->
      <div v-if="activeTab === 'goals'" class="goals-tab">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <span>Loading goals...</span>
        </div>

        <template v-else>
          <div class="goals-section">
            <h3>Nutrition Goals</h3>

            <div class="goals-grid">
              <div class="goal-card">
                <span class="goal-label">Daily Calories</span>
                <span class="goal-value">{{ goalsData.calories || 2000 }} kcal</span>
              </div>
              <div class="goal-card">
                <span class="goal-label">Carbs Target</span>
                <span class="goal-value">{{ goalsData.carbs || 250 }}g</span>
              </div>
              <div class="goal-card">
                <span class="goal-label">Protein Target</span>
                <span class="goal-value">{{ goalsData.protein || 100 }}g</span>
              </div>
              <div class="goal-card">
                <span class="goal-label">Fat Target</span>
                <span class="goal-value">{{ goalsData.fat || 65 }}g</span>
              </div>
              <div class="goal-card">
                <span class="goal-label">Hydration Target</span>
                <span class="goal-value">{{ goalsData.hydration || 2500 }}ml</span>
              </div>
            </div>

            <!-- Dietary restrictions -->
            <div v-if="goalsData.restrictions && goalsData.restrictions.length > 0" class="restrictions-section">
              <h4>Dietary Restrictions</h4>
              <div class="restrictions-list">
                <span
                  v-for="restriction in goalsData.restrictions"
                  :key="restriction"
                  class="restriction-tag"
                >
                  {{ restriction }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="activeTab === 'estimate'" class="estimate-tab">
        <FuellingEstimate :player="player" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";
import NutritionProgress from "./NutritionProgress.vue";
import FuellingEstimate from "./FuellingEstimate.vue";

export default {
  name: "PlayerNutritionDetail",

  components: {
    NutritionProgress,
    FuellingEstimate,
  },

  props: {
    player: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      activeTab: "today",
      currentDate: new Date().toISOString().split("T")[0],
      showDatePicker: false,
      dailyData: null,
      meals: [],
      hydrationLogs: [],
      weeklyData: {},
      goalsData: {},
      tabs: [
        { id: "today", label: "Today" },
        { id: "week", label: "Week" },
        { id: "goals", label: "Goals" },
        { id: "estimate", label: "Fuelling estimate" },
      ],
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isToday() {
      const today = new Date().toISOString().split("T")[0];
      return this.currentDate === today;
    },

    hydrationTotal() {
      return this.dailyData?.totals?.total_hydration || 0;
    },

    hydrationTarget() {
      return this.dailyData?.targets?.hydration || 2500;
    },

    hydrationPercent() {
      if (!this.hydrationTarget) return 0;
      return Math.min(100, Math.round((this.hydrationTotal / this.hydrationTarget) * 100));
    },

    weekDays() {
      const days = [];
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const dateStr = date.toISOString().split("T")[0];
        const dayData = this.weeklyData.days?.find((d) => d.date === dateStr) || {};

        days.push({
          date: dateStr,
          dayName: dayNames[date.getDay()],
          dayNum: date.getDate(),
          isToday: dateStr === new Date().toISOString().split("T")[0],
          mealsLogged: dayData.meals_logged || 0,
          hydrationPct: dayData.hydration_pct || 0,
          compliance: dayData.compliance || 0,
        });
      }

      return days;
    },
  },

  watch: {
    activeTab() {
      this.loadTabData();
    },
    currentDate() {
      this.loadTabData();
    },
    player: {
      handler() {
        this.loadTabData();
      },
      immediate: true,
    },
  },

  mounted() {
    this.loadTabData();
  },

  methods: {
    goBack() {
      this.$emit("back");
    },

    changeDate(delta) {
      const date = new Date(this.currentDate);
      date.setDate(date.getDate() + delta);
      const newDate = date.toISOString().split("T")[0];
      const today = new Date().toISOString().split("T")[0];

      if (newDate <= today) {
        this.currentDate = newDate;
      }
    },

    selectDay(dateStr) {
      this.currentDate = dateStr;
      this.activeTab = "today";
    },

    async loadTabData() {
      this.loading = true;
      try {
        switch (this.activeTab) {
          case "today":
            await this.loadDailyData();
            break;
          case "week":
            await this.loadWeeklyData();
            break;
          case "goals":
            await this.loadGoalsData();
            break;
        }
      } catch (error) {
        console.error("Error loading tab data:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadDailyData() {
      try {
        const response = await Nutrition.getPlayerNutritionForAdmin(
          this.getProfile?.id,
          this.player.id,
          this.currentDate
        );

        if (response.data?.success) {
          this.dailyData = response.data.daily_plan || {};
          this.meals = response.data.meals || [];
          this.hydrationLogs = response.data.hydration_logs || [];
        }
      } catch (error) {
        console.error("Error loading daily data:", error);
      }
    },

    async loadWeeklyData() {
      try {
        // Calculate start of week
        const date = new Date(this.currentDate);
        const dayOfWeek = date.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const startDate = new Date(date);
        startDate.setDate(date.getDate() + diff);

        const response = await Nutrition.getWeeklyPlan(
          this.player.id,
          startDate.toISOString().split("T")[0],
          {}
        );

        if (response.data?.success) {
          this.weeklyData = response.data || {};
        }
      } catch (error) {
        console.error("Error loading weekly data:", error);
      }
    },

    async loadGoalsData() {
      try {
        const response = await Nutrition.getPlayerGoals(this.player.id);

        if (response.data?.success) {
          this.goalsData = response.data.goals || {};
        }
      } catch (error) {
        console.error("Error loading goals:", error);
      }
    },

    formatDateDisplay(dateStr) {
      const date = new Date(dateStr);
      const options = { weekday: "short", month: "short", day: "numeric" };
      return date.toLocaleDateString("en-GB", options);
    },

    formatTime(timeStr) {
      if (!timeStr) return "";
      const [hours, mins] = timeStr.split(":");
      const h = parseInt(hours, 10);
      const suffix = h >= 12 ? "pm" : "am";
      const displayHour = h % 12 || 12;
      return `${displayHour}:${mins} ${suffix}`;
    },

    formatValue(value) {
      if (value === null || value === undefined) return "0";
      return Math.round(value);
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getMealTypeInfo(mealType) {
      return Nutrition.getMealTypeInfo(mealType);
    },

    getMealIconStyle(mealType) {
      const info = this.getMealTypeInfo(mealType);
      return { backgroundColor: info.color };
    },

    getMealSymbol(mealType) {
      const info = this.getMealTypeInfo(mealType);
      return info.symbol;
    },

    getMealLabel(mealType) {
      const info = this.getMealTypeInfo(mealType);
      return info.label;
    },

    getDayStatusStyle(day) {
      if (day.mealsLogged === 0) {
        return { backgroundColor: "#e9ecef", color: "#666" };
      } else if (day.compliance >= 80) {
        return { backgroundColor: "#d4edda", color: "#28a745" };
      } else if (day.compliance >= 50) {
        return { backgroundColor: "#fff3cd", color: "#856404" };
      }
      return { backgroundColor: "#f8d7da", color: "#dc3545" };
    },
  },
};
</script>

<style scoped lang="scss">
.player-nutrition-detail {
  background: #f5f5f5;
  min-height: 100vh;
}

.detail-header {
  background: white;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #007bff;
  font-size: 0.9rem;
  padding: 0;
  cursor: pointer;
  margin-bottom: 12px;

  &:hover {
    color: #0056b3;
  }
}

.player-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.player-avatar {
  .avatar-img {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1.1rem;
  }
}

.player-meta {
  .player-name {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
  }

  .player-team {
    font-size: 0.9rem;
    color: #666;
  }
}

.date-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #eee;
}

.date-nav {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f0f0f0;
    color: #333;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.date-display {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  .date-label {
    font-weight: 600;
    color: #333;
  }

  .today-badge {
    background: #007bff;
    color: white;
    font-size: 0.7rem;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;
  }
}

.detail-tabs {
  display: flex;
  background: white;
  border-bottom: 1px solid #eee;
  padding: 0 16px;
}

.tab-btn {
  flex: 1;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 12px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #333;
  }

  &.active {
    color: #007bff;
    border-bottom-color: #007bff;
  }
}

.tab-content {
  padding: 16px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e9ecef;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.quick-summary {
  margin-bottom: 16px;
}

.hydration-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 1rem;
      color: #333;
    }

    .hydration-value {
      font-size: 0.9rem;
      color: #666;
    }
  }

  .hydration-bar {
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  .hydration-fill {
    height: 100%;
    background: linear-gradient(90deg, #17a2b8, #20c997);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .hydration-percent {
    font-size: 0.85rem;
    font-weight: 600;
    color: #17a2b8;
  }
}

.meals-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h3 {
    margin: 0 0 12px;
    font-size: 1rem;
    color: #333;
  }
}

.meals-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.meal-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.meal-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.8rem;
}

.meal-info {
  flex: 1;
  min-width: 0;

  .meal-name {
    display: block;
    font-weight: 600;
    color: #333;
    font-size: 0.9rem;
  }

  .meal-time {
    font-size: 0.8rem;
    color: #666;
  }
}

.meal-macros {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.empty-meals {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 0.9rem;
}

/* Week tab */
.week-summary {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .week-header {
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 1rem;
      color: #333;
    }
  }
}

.week-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.day-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  background: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
  }

  &.today {
    background: #cce5ff;
    border: 1px solid #b8daff;
  }

  .day-name {
    font-size: 0.7rem;
    color: #666;
    text-transform: uppercase;
  }

  .day-date {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 4px 0;
  }

  .day-status {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;

    .day-empty {
      opacity: 0.5;
    }
  }
}

.week-totals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.total-item {
  text-align: center;

  .total-label {
    display: block;
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 4px;
  }

  .total-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
  }
}

/* Goals tab */
.goals-section {
  h3 {
    margin: 0 0 16px;
    font-size: 1rem;
    color: #333;
  }
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.goal-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;

  .goal-label {
    display: block;
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 8px;
  }

  .goal-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
  }
}

.restrictions-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h4 {
    margin: 0 0 12px;
    font-size: 0.9rem;
    color: #333;
  }
}

.restrictions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.restriction-tag {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  color: #666;
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .goals-grid {
    grid-template-columns: 1fr;
  }

  .week-days {
    gap: 4px;
  }

  .day-card {
    padding: 6px;

    .day-name {
      font-size: 0.6rem;
    }

    .day-date {
      font-size: 0.9rem;
    }

    .day-status {
      width: 20px;
      height: 20px;
      font-size: 0.65rem;
    }
  }

  .week-totals {
    gap: 8px;

    .total-value {
      font-size: 1.1rem;
    }
  }
}
</style>
